import { Query } from '@apollo/client/react/components';
/*
before touching this file
PLEASE READ
./README.refactoringInProgress.md
*/

import * as React from 'react';
import ErrorReporter from '../../../app/components/ErrorReporter';
import { ClassSearchability } from 'globalTypes';

import CLASS_SETTINGS_QUERY from './CLASS_SETTINGS_QUERY.gql';
import type { ClassSettings, ClassSettingsVariables } from './__generated__/ClassSettings';

import Under13Checkbox from './Under13Checkbox';
import ClassSearchabilityContainer from './ClassSearchabilityContainer/ClassSearchabilityContainer';
import RequestToJoinContainer from './RequestToJoinContainer/RequestToJoinContainer';
import ParticipantMessagingSelectContainer from './ParticipantMessagingSelectContainer/ParticipantMessagingSelectContainer';

import LoadingIndicator from 'components/LoadingIndicator';

import './GroupOptionsForm.scss';
import Notice from 'components/Notice';
import Text from 'components/Text';
import { FormattedMessage } from 'lib/intl';

/**
 * Parent-owned form values. `null` for edit-only fields means “use backend until the user changes it.”
 */
export type GroupOptionsValues = {
  hasChildren: boolean;
  classSearchabilityEnabled: ClassSearchability | null;
  requestToJoinEnabled: boolean | null;
  participantMessaging: string | null;
};

export type GroupOptionsField = keyof GroupOptionsValues;

export const defaultGroupOptionsValues = (
  overrides: Partial<GroupOptionsValues> = {}
): GroupOptionsValues => ({
  hasChildren: false,
  classSearchabilityEnabled: null,
  requestToJoinEnabled: null,
  participantMessaging: null,
  ...overrides,
});

type Props = {
  values: GroupOptionsValues;
  onChange?:
    | ((name: GroupOptionsField, value: GroupOptionsValues[GroupOptionsField]) => void)
    | null;
  groupUuid?: string | null;
  showClassSearchability?: boolean;
  canCreateU13?: boolean;
};

/**
 * https://app.asana.com/0/740899735370516/1202989613759303/f
 */
const shouldDisableSettings = (settingsLocked: boolean, districtId?: number) => {
  return settingsLocked || districtId === 534144;
};

/**
 * Stateless options shell: form field values live in the parent via `values` / `onChange`.
 */
function GroupOptionsForm({
  values,
  showClassSearchability = false,
  groupUuid = '',
  canCreateU13 = false,
  onChange = null,
}: Props) {
  const hasChildren = !!values.hasChildren;

  const emit = (name: GroupOptionsField, value: GroupOptionsValues[GroupOptionsField]) => {
    if (onChange) {
      onChange(name, value);
    }
  };

  const toggleHasChildren = () => {
    emit('hasChildren', !hasChildren);
  };

  const under13Checked = true;

  const under13Disabled = !canCreateU13;

  if (!groupUuid) {
    return (
      <Under13Checkbox
        checked={under13Checked}
        disabled={under13Disabled}
        hasChildren={hasChildren}
        toggle={toggleHasChildren}
        isModal={true}
      />
    );
  }

  return (
    <Query<ClassSettings, ClassSettingsVariables>
      query={CLASS_SETTINGS_QUERY}
      variables={{ groupUuid }}
      fetchPolicy="network-only"
    >
      {({ loading, error, data }) => {
        if (loading && !data?.classSettingsScreen) {
          return <LoadingIndicator />;
        }

        if (error) {
          console.error('GroupOptionsForm - unable to load class settings', error);
          return <ErrorReporter error="GroupOptionsForm - unable to load class settings" />;
        }

        // Apollo has this in-between state of not loading and not providing any data
        if (!data?.classSettingsScreen) {
          return null;
        }

        const {
          canEditParticipantMessagingState,
          participantMessagingState,
          participantMessagingSchoolRecommendation,
          canEditSearchability,
          searchability,
          searchabilitySchoolRecommendation,
          requestToJoinEnabled: requestToJoinEnabledFromBackend,
          requestToJoinEnabledRecommendation,
          canEditWillOnlyMessage13AndOlder,
          organization,
          areClassSettingsLockedByOrg,
        } = data.classSettingsScreen;

        const districtId = organization?.parentId ?? undefined;
        const disableSettings = shouldDisableSettings(areClassSettingsLockedByOrg, districtId);

        return (
          <React.Fragment>
            {areClassSettingsLockedByOrg && (
              <Text element="p">
                <Notice color="whiteout">
                  <FormattedMessage id="admin.classSettings.lockSettings.locked.class" />
                </Notice>
              </Text>
            )}
            {showClassSearchability && canEditSearchability && (
              <ClassSearchabilityContainer
                disabled={disableSettings}
                classSearchabilityEnabled={values.classSearchabilityEnabled}
                setClassSearchabilityEnabled={(searchabilityValue: ClassSearchability) =>
                  emit('classSearchabilityEnabled', searchabilityValue)
                }
                classSearchabilityFromBackend={searchability}
                classSearchabilitySchoolDefault={searchabilitySchoolRecommendation || ''}
                groupUuid={groupUuid}
              />
            )}
            <RequestToJoinContainer
              disabled={disableSettings}
              requestToJoinFromBackend={requestToJoinEnabledFromBackend}
              requestToJoinEnabledRecommendation={requestToJoinEnabledRecommendation || ''}
              requestToJoinFromFrontend={values.requestToJoinEnabled}
              groupUuid={groupUuid}
              setRequestToJoinEnabled={(enabled) => emit('requestToJoinEnabled', enabled)}
            />
            {canEditParticipantMessagingState && (
              <ParticipantMessagingSelectContainer
                disabled={disableSettings}
                participantMessagingFromBackend={participantMessagingState}
                participantMessagingSchoolDefault={participantMessagingSchoolRecommendation || ''}
                groupUuid={groupUuid}
                participantMessagingFromFrontend={values.participantMessaging}
                setParticipantMessagingValue={(participantMessagingValue: string) => {
                  emit('participantMessaging', participantMessagingValue);
                }}
              />
            )}
            {canEditWillOnlyMessage13AndOlder && canCreateU13 && (
              <Under13Checkbox
                checked={under13Checked}
                disabled={under13Disabled || areClassSettingsLockedByOrg}
                hasChildren={hasChildren}
                toggle={toggleHasChildren}
                isModal={false}
              />
            )}
          </React.Fragment>
        );
      }}
    </Query>
  );
}

export default GroupOptionsForm;
